import { useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ivoryHealthLogo from "@/assets/ivory-health-logo.webp";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/context/AdminAuthContext";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, sendMobileOtp, verifyMobileOtp } = useAdminAuth();
  // Same two routes as the employee sign-in: work email, or a mobile already
  // on the staff record.
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [previewCode, setPreviewCode] = useState("");
  const [stage, setStage] = useState("email");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobile = method === "mobile";
  const identity = isMobile ? mobile : email;

  function switchMethod(next) {
    setMethod(next);
    setError("");
    setCode("");
    setPreviewCode("");
  }

  async function submitIdentity(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      const response = await (isMobile ? sendMobileOtp(mobile) : sendOtp(email));
      setPreviewCode(response.preview_otp || "");
      setStage("code");
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitCode(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await (isMobile ? verifyMobileOtp(mobile, code) : verifyOtp(email, code));
      navigate("/admin");
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-page" data-testid="admin-login-page">
      <section className="login-hero" data-testid="admin-login-hero">
        <div className="login-brand" data-testid="admin-login-brand"><img src={ivoryHealthLogo} alt="Ivory Health" className="login-brand__logo" /><strong>Ivory Health</strong></div>
        <p className="eyebrow">Ops dashboard</p>
        <h1>Every client, one view.</h1>
        <p>Service requests across all your corporate clients, in one place.</p>
      </section>
      <section className="login-panel" data-testid="admin-login-panel">
        <div className="security-note" data-testid="admin-secure-login-note"><ShieldCheck size={18} />Secure ops sign-in</div>
        {stage === "email" ? (
          <form onSubmit={submitIdentity} className="login-form" data-testid="admin-email-login-form">
            <h2>Ivory ops sign-in</h2>
            <div className="login-method-switch" role="tablist" aria-label="Sign-in method">
              <button type="button" role="tab" aria-selected={!isMobile} className={!isMobile ? "is-active" : ""} onClick={() => switchMethod("email")} data-testid="admin-login-method-email">Work email</button>
              <button type="button" role="tab" aria-selected={isMobile} className={isMobile ? "is-active" : ""} onClick={() => switchMethod("mobile")} data-testid="admin-login-method-mobile">Mobile</button>
            </div>
            {isMobile ? (
              <>
                <p>Enter the mobile number registered against your ops account to receive a one-time code.</p>
                <label htmlFor="admin-work-mobile">Mobile number</label>
                <input id="admin-work-mobile" inputMode="tel" maxLength="10" value={mobile} onChange={(event) => setMobile(event.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile number" required data-testid="admin-work-mobile-input" />
              </>
            ) : (
              <>
                <p>Enter your Ivory Health email to receive a one-time verification code.</p>
                <label htmlFor="admin-work-email">Work email</label>
                <input id="admin-work-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@ivoryhealth.com" required data-testid="admin-work-email-input" />
              </>
            )}
            {error && <p className="form-error" role="alert" data-testid="admin-login-error-message">{error}</p>}
            <Button type="submit" disabled={isSubmitting || (isMobile && mobile.length !== 10)} data-testid="admin-request-otp-button">{isSubmitting ? "Preparing code…" : <>Continue <ArrowRight size={18} /></>}</Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="login-form" data-testid="admin-otp-verification-form">
            <button className="text-button" type="button" onClick={() => setStage("email")} data-testid="admin-change-email-button">{isMobile ? "Use another number" : "Use another email"}</button>
            <h2>{isMobile ? "Check your messages" : "Check your inbox"}</h2>
            <p>A six-digit code was requested for <strong data-testid="admin-verification-email">{identity}</strong>.</p>
            {previewCode && <p className="preview-code" data-testid="admin-preview-otp-code">Preview code: <strong>{previewCode}</strong></p>}
            <label htmlFor="admin-verification-code">Verification code</label>
            <input id="admin-verification-code" inputMode="numeric" maxLength="6" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="000000" required data-testid="admin-otp-code-input" />
            {error && <p className="form-error" role="alert" data-testid="admin-otp-error-message">{error}</p>}
            <Button type="submit" disabled={isSubmitting || code.length !== 6} data-testid="admin-verify-otp-button">{isSubmitting ? "Verifying…" : "Let's go"}</Button>
          </form>
        )}
      </section>
    </main>
  );
}
